import { useState } from 'react';
import { Box, Chip, IconButton, Popover } from '@mui/material';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import type { MessageReaction } from '../../types/help-request.types';
import { helpRequestService } from '../../services/help-request.service';

const REACTION_EMOJIS = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{1F389}', '\u{1F602}', '\u{1F914}', '\u{270B}', '\u{1F440}', '\u{1F64F}'];

interface GroupedReaction {
  emoji: string;
  count: number;
  userIds: string[];
  names: string[];
}

interface MessageReactionsProps {
  helpRequestId: string;
  messageId: string;
  reactions: MessageReaction[];
  currentUserId: string;
  onReactionsChange: (messageId: string, reactions: MessageReaction[]) => void;
}

function groupReactions(reactions: MessageReaction[]): GroupedReaction[] {
  const map = new Map<string, GroupedReaction>();
  for (const r of reactions) {
    const existing = map.get(r.emoji);
    if (existing) {
      existing.count++;
      existing.userIds.push(r.userId);
      if (r.user) {
        existing.names.push(`${r.user.firstName || ''} ${r.user.lastName || ''}`.trim());
      }
    } else {
      map.set(r.emoji, {
        emoji: r.emoji,
        count: 1,
        userIds: [r.userId],
        names: r.user ? [`${r.user.firstName || ''} ${r.user.lastName || ''}`.trim()] : [],
      });
    }
  }
  return Array.from(map.values());
}

export default function MessageReactions({
  helpRequestId,
  messageId,
  reactions,
  currentUserId,
  onReactionsChange,
}: MessageReactionsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);

  const grouped = groupReactions(reactions);

  const handleToggleReaction = async (emoji: string) => {
    if (loading) return;
    setLoading(true);
    setAnchorEl(null);
    try {
      const userAlreadyReacted = reactions.some(
        (r) => r.emoji === emoji && r.userId === currentUserId,
      );
      const updated = userAlreadyReacted
        ? await helpRequestService.removeReaction(helpRequestId, messageId, emoji)
        : await helpRequestService.addReaction(helpRequestId, messageId, emoji);
      onReactionsChange(messageId, updated);
    } catch {
      // silently ignore — reaction will sync via WS
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.5,
        minHeight: grouped.length > 0 ? 28 : 0,
      }}
    >
      {grouped.map((g) => {
        const isMine = g.userIds.includes(currentUserId);
        return (
          <Chip
            key={g.emoji}
            label={`${g.emoji} ${g.count}`}
            size="small"
            variant={isMine ? 'filled' : 'outlined'}
            onClick={() => handleToggleReaction(g.emoji)}
            disabled={loading}
            sx={{
              height: 24,
              borderRadius: '12px',
              fontSize: '0.75rem',
              cursor: 'pointer',
              bgcolor: isMine ? 'rgba(0,150,136,0.12)' : 'transparent',
              borderColor: isMine ? 'rgba(0,150,136,0.5)' : 'divider',
              '&:hover': {
                bgcolor: isMine ? 'rgba(0,150,136,0.2)' : 'action.hover',
              },
            }}
          />
        );
      })}

      <IconButton
        size="small"
        className="reaction-add-btn"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          opacity: grouped.length > 0 ? 0 : 0,
          transition: 'opacity 0.15s',
          width: 24,
          height: 24,
          fontSize: 14,
        }}
      >
        <AddReactionOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '16px', p: 0.5 } } }}
      >
        <Box sx={{ display: 'flex', gap: 0.25, p: 0.5 }}>
          {REACTION_EMOJIS.map((emoji) => {
            const alreadyReacted = reactions.some(
              (r) => r.emoji === emoji && r.userId === currentUserId,
            );
            return (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => handleToggleReaction(emoji)}
                disabled={loading}
                sx={{
                  fontSize: 18,
                  width: 32,
                  height: 32,
                  bgcolor: alreadyReacted ? 'rgba(0,150,136,0.12)' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover', transform: 'scale(1.2)' },
                  transition: 'transform 0.1s',
                }}
              >
                {emoji}
              </IconButton>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
}
